import { useEffect, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import ArticleList from "@/components/ArticleList/ArticleList";
import { ContentBlock } from "@/components/ContentBlock/ContentBlock";
import { ContentGroup } from "@/components/ContentGroup/ContentGroup";
import { GET } from "@/services/api/client";
import { envVar } from "@/services/env";
import { layoutEmitter } from "@/services/layoutUtils";
import { useHybridRouter } from "@/services/router/useHybridRouter";
import { htmlify } from "@/services/utils";
import type { FundDetailPagePathProps } from "@/types/FundDetailPagePathProps";
import type { GetArticlesEndpoint } from "@/types/endpoints/GetArticlesEndpoint";
import type { FundDetailLayoutComponentProps } from "@/types/fundDetailLayout";

// currently, this uses both the new endpoint AND the old dashboard.
// Either this needs to be made smarter or core-api needs to support legacy article fetching
// right now, it assumes, if it receives props and no articles, that it should fetch from core-api
export const FxCommentaryPanel = ({ dashboard }: FundDetailLayoutComponentProps) => {
	const { fxCommentaryPanel: props, shareClass } = dashboard;
	const { title, tooltip, articles: originalArticles, useExternalSource, bgColor } = props || {};
	const [articles, setArticles] = useState(useExternalSource ? undefined : originalArticles);
	const [loading, setLoading] = useState(useExternalSource);
	const { externalId } = shareClass;

	const {
		query: { location, channel, language }
	} = useHybridRouter<FundDetailPagePathProps>();

	useEffect(() => {
		if (!useExternalSource) {
			return;
		}

		const getArticles = async () => {
			try {
				const response = await GET<GetArticlesEndpoint>(`${envVar("PUBLIC_ENV__API_HOST")}/api/v1/getArticles`, {
					location,
					channel,
					language,
					...(shareClass as any)
				});

				if (response.isErr) {
					setArticles([]);
					setLoading(false);
					return;
				}

				const { articles } = response.body.data;
				const newArticles = articles.length > 0 ? articles.slice(0, 3) : undefined;

				setArticles(newArticles);
				setLoading(false);

				if (newArticles) {
					layoutEmitter.emit("SectionRendered", "#fxCommentaryPanel");
				}
			} catch (e) {
				setArticles(undefined);
				setLoading(false);
			}
		};

		getArticles();
	}, [useExternalSource, externalId, shareClass, location, channel, language]);

	if (!props || !articles || articles.length < 1) {
		return null;
	}
	return (
		<ContentGroup id="fxCommentaryPanel" data-testid="FxCommentaryPanel" bgColor={bgColor || "#F6A72B"} tabIndex={-1}>
			<ContentBlock heading={htmlify(title)} tooltip={htmlify(tooltip)}>
				{loading && <Spinner marginInline="auto" />}
				{!loading && Array.isArray(articles) && <ArticleList {...props} articles={articles} />}
			</ContentBlock>
		</ContentGroup>
	);
};
